import { useMemo } from "react";
import { Users, TrendingUp, IndianRupee } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  CompactStatCard,
  PageHeader,
  StatsGrid,
} from "@/components/shared";
import { useReferralLink, useReferralNetwork, useReferralSummary } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { ReferralCodeCard, ReferralTeamTable } from "@/components/account/ReferralTeamShared";

const AccountDirectTeam = () => {
  const { data: summary, isLoading: summaryLoading } = useReferralSummary();
  const { data: referralLink } = useReferralLink();
  const { data: network = [], isLoading: networkLoading } = useReferralNetwork();

  const directTeam = useMemo(() => network.filter((r) => r.level === 1), [network]);
  const referralCode = referralLink?.referralCode ?? "—";

  const directCommission = useMemo(
    () => directTeam.reduce((sum, row) => sum + (row.commissionEarned || 0), 0),
    [directTeam],
  );

  const level1Summary = summary?.levelWiseSummary?.find((l) => l.level === 1);
  const directCount = level1Summary?.referralCount ?? directTeam.length;
  const earned = level1Summary?.totalEarnings ?? directCommission;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Direct Team"
        description="Members who joined directly with your referral code."
      />

      <ReferralCodeCard referralLink={referralLink} referralCode={referralCode} />

      <StatsGrid columns={3}>
        <CompactStatCard
          title="Direct Members"
          value={summaryLoading && networkLoading ? "…" : directCount}
          icon={Users}
        />
        <CompactStatCard
          title="Direct Earnings"
          value={summaryLoading && networkLoading ? "…" : `₹${Number(earned).toFixed(0)}`}
          icon={IndianRupee}
          iconClassName="bg-gradient-primary"
        />
        <CompactStatCard
          title="Active Direct"
          value={
            networkLoading
              ? "…"
              : directTeam.filter((r) => r.totalOrders > 0 || r.commissionEarned > 0).length
          }
          icon={TrendingUp}
        />
      </StatsGrid>

      {networkLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : directTeam.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          No direct team members yet. Share your referral code to get started.
        </Card>
      ) : (
        <ReferralTeamTable
          title="Direct Referral"
          data={directTeam}
          emptyMessage="No direct team members found."
        />
      )}
    </div>
  );
};

export default AccountDirectTeam;
